import React from "react";
import { ArrowUpRight, Swords, Cpu, Rocket, Shield, Radar, Flame } from "lucide-react";

const APPS = [
  {
    klingon: "batlh'a'",
    es: "El Gran Honor",
    desc: "Sistema de reputación inquebrantable. Cada commit firmado con sangre digital. Auditoría on-chain estilo guerrero.",
    tag: "REPUTATION",
    icon: Shield,
    accent: "#FF2A2A",
    rune: "ᛟ",
    span: "lg:col-span-7 lg:row-span-2",
    feature: true,
  },
  {
    klingon: "HoSghaj",
    es: "El Poderoso",
    desc: "Motor de cómputo. Procesa más rápido que un crucero D7 en aproximación.",
    tag: "COMPUTE",
    icon: Cpu,
    accent: "#E6B836",
    rune: "ᚷ",
    span: "lg:col-span-5",
  },
  {
    klingon: "may'Duj",
    es: "Nave de Guerra",
    desc: "DevOps battle cruiser. Despliegues sin testigos. Sin retorno.",
    tag: "DEVOPS",
    icon: Rocket,
    accent: "#C59B27",
    rune: "ᛏ",
    span: "lg:col-span-5",
  },
  {
    klingon: "Qey'",
    es: "Estricto / Tenso",
    desc: "Linter sin piedad. Tu código se inclinará o caerá. No hay punto medio.",
    tag: "LINTER",
    icon: Swords,
    accent: "#FF6A6A",
    rune: "ᛗ",
    span: "lg:col-span-4",
  },
  {
    klingon: "DaH",
    es: "Ahora",
    desc: "Pipeline de datos en tiempo real. Hoy. Sin retraso. Sin compasión.",
    tag: "STREAMING",
    icon: Radar,
    accent: "#FF2A2A",
    rune: "ᛞ",
    span: "lg:col-span-4",
  },
  {
    klingon: "veSDuj",
    es: "Crucero de Combate",
    desc: "CI/CD que no negocia. Si los tests caen, el pull request muere.",
    tag: "CI/CD",
    icon: Flame,
    accent: "#E6B836",
    rune: "ᛇ",
    span: "lg:col-span-4",
  },
];

const AppCard = ({ app, idx }) => {
  const Icon = app.icon;
  return (
    <article
      data-testid={`app-card-${app.klingon}`}
      className={`k-card group p-7 md:p-8 ${app.span || ""} ${app.feature ? "min-h-[420px]" : "min-h-[260px]"} flex flex-col justify-between`}
    >
      {/* Corner runes */}
      <span
        aria-hidden
        className="absolute top-3 right-3 font-rune text-2xl opacity-40 group-hover:opacity-100 transition-opacity"
        style={{ color: app.accent }}
      >
        {app.rune}
      </span>

      {/* Header */}
      <div className="flex items-start justify-between gap-4 z-10 relative">
        <div className="flex items-center gap-4">
          <div
            className="w-12 h-12 flex items-center justify-center border"
            style={{
              borderColor: app.accent + "55",
              background: "rgba(0,0,0,0.6)",
              clipPath: "polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)",
            }}
          >
            <Icon size={22} color={app.accent} strokeWidth={1.6} />
          </div>
          <div className="font-mono-k text-[10px] uppercase tracking-[0.35em] text-[#A0A0A0]">
            <div>// app.{String(idx + 1).padStart(2, "0")}</div>
            <div className="text-[#555]">{app.tag}</div>
          </div>
        </div>
        <ArrowUpRight
          size={20}
          className="text-[#555] group-hover:text-[#E6B836] group-hover:rotate-45 transition-all duration-300 flex-shrink-0"
        />
      </div>

      {/* Title */}
      <div className="mt-8 z-10 relative">
        <h3
          className={`font-display font-black uppercase leading-[0.9] text-[#EAEAEA] ${
            app.feature ? "text-5xl md:text-7xl" : "text-3xl md:text-4xl"
          }`}
        >
          {app.klingon}
        </h3>
        <div
          className="mt-2 font-cinzel italic text-base md:text-lg"
          style={{ color: app.accent }}
        >
          “{app.es}”
        </div>
      </div>

      {/* Description */}
      <p
        className={`mt-6 font-mono-k text-[#A0A0A0] leading-relaxed z-10 relative ${
          app.feature ? "text-base md:text-lg max-w-lg" : "text-sm"
        }`}
      >
        {app.desc}
      </p>

      {/* Footer line */}
      <div className="mt-6 pt-4 border-t border-[#2A0A0A] flex items-center justify-between font-mono-k text-[10px] uppercase tracking-[0.3em] z-10 relative">
        <span className="text-[#555]">forjado · bicio</span>
        <span className="text-[#C59B27] group-hover:text-[#FF2A2A] transition-colors">
          desplegar →
        </span>
      </div>

      {/* Hover blood wash */}
      <div
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
        style={{
          background: `radial-gradient(circle at top right, ${app.accent}22 0%, transparent 60%)`,
        }}
      />
    </article>
  );
};

const AppsShowcase = () => {
  return (
    <section
      id="apps"
      data-testid="apps-section"
      className="relative bg-[#050505] py-24 md:py-32 px-6 md:px-12 overflow-hidden border-b border-[#2A0A0A]"
    >
      {/* Background rune */}
      <div className="absolute right-0 top-20 font-rune text-[24rem] leading-none text-[#8A0303] opacity-[0.04] select-none pointer-events-none">
        ⌬
      </div>

      <div className="max-w-7xl mx-auto relative">
        {/* Section header */}
        <div className="mb-14 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="bar-bronze" />
              <span className="font-mono-k text-[11px] uppercase tracking-[0.4em] text-[#C59B27]">
                arsenal&nbsp;//&nbsp;may'mey&nbsp;chu'
              </span>
            </div>
            <h2
              data-testid="apps-title"
              className="font-display font-black uppercase text-4xl md:text-5xl lg:text-6xl leading-[0.95] text-[#EAEAEA]"
            >
              Apps forjadas por <span className="text-[#FF2A2A]">Bicio</span>
            </h2>
            <p className="mt-4 max-w-2xl font-mono-k text-[#A0A0A0] text-sm md:text-base leading-relaxed">
              Seis armas digitales. Cada una llamada por su nombre klingon, cada una
              templada en producción. Sin tutoriales. Sin red de seguridad. Sólo
              <span className="text-[#E6B836]">&nbsp;qapla'</span>.
            </p>
          </div>
          <div className="flex items-center gap-3 font-mono-k text-[10px] uppercase tracking-[0.3em] text-[#A0A0A0]">
            <span className="h-2 w-2 rounded-full bg-[#FF2A2A] animate-pulse" />
            06 / 06 desplegadas
          </div>
        </div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-5 lg:gap-6 auto-rows-min" data-testid="apps-grid">
          {APPS.map((app, idx) => (
            <AppCard key={app.klingon} app={app} idx={idx} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default AppsShowcase;
